import { Shield } from "lucide-react"
import { useLang } from "../context/LangContext"
import { LegalPage } from "./LegalShared"

const CONTENT = {
  en: {
    title: "Privacy Policy",
    updated: "Last updated: April 2026",
    intro: 'uCloud360 ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and protect your information when you access our platform at https://www.ucloud360.com.',
    sections: [
      {
        h: "Who We Are",
        body: "uCloud360 is a digital incubation and acceleration platform operated by Inteligentic Solutions LLC, Miami, FL. We provide collaboration tools, educational resources, and proprietary technology utilities to help Hispanic entrepreneurs in the United States."
      },
      {
        h: "Information We Collect",
        bullets: [
          "Account information: name, email address, and profile data you provide.",
          "Authentication data via Supabase Auth and optional third-party OAuth providers.",
          "Usage data related to tools and platform features (time tracker, calculators, etc.).",
          "Technical data: IP address, browser type, device information, and cookies.",
          "Billing data is processed exclusively by Stripe — we do not store payment card details."
        ]
      },
      {
        h: "How We Use Your Information",
        bullets: [
          "To provide, maintain, and improve the platform and its features.",
          "To authenticate your identity and manage your account.",
          "To process subscription payments through Stripe.",
          "To send transactional emails (e.g. email verification, password reset).",
          "To analyze usage patterns and improve platform performance."
        ]
      },
      {
        h: "Cookies & Tracking",
        body: "We use cookies and similar technologies to authenticate users, maintain sessions, and analyze platform performance. You may disable cookies in your browser settings, although this may affect platform functionality."
      },
      {
        h: "Embedded Third-Party Content",
        body: "Our platform includes embedded content from CoinGecko (crypto market data) and Investing.com (economic calendar). These services may collect data according to their own privacy policies. We are not responsible for third-party data practices."
      },
      {
        h: "Data Storage & International Transfers",
        body: "Your information is stored on Supabase infrastructure (hosted on AWS). It may be processed in the United States or other countries where our service providers operate. By using uCloud360, you consent to this transfer."
      },
      {
        h: "Your Rights (CCPA & General)",
        bullets: [
          "Right to access: request a copy of the personal data we hold about you.",
          "Right to correction: request correction of inaccurate data.",
          "Right to deletion: request deletion of your account and associated data.",
          "Right to opt out: opt out of any non-essential data processing.",
          "To exercise these rights, email info@ucloud360.com."
        ]
      },
      {
        h: "Children's Privacy",
        body: "uCloud360 is not intended for children under 13 years of age. We do not knowingly collect personal information from minors."
      },
      {
        h: "Changes to This Policy",
        body: "We may update this Privacy Policy periodically. We will notify users of material changes by updating the date at the top of this page. Continued use of the platform constitutes acceptance of the updated policy."
      },
      {
        h: "Contact",
        body: "For privacy-related inquiries: info@ucloud360.com | uCloud360 · Inteligentic Solutions LLC · Miami, FL"
      }
    ]
  },
  es: {
    title: "Política de Privacidad",
    updated: "Última actualización: Abril 2026",
    intro: 'uCloud360 ("nosotros", "nuestro") está comprometido con la protección de tu privacidad. Esta Política explica cómo recopilamos, usamos, almacenamos y protegemos tu información al acceder a https://www.ucloud360.com.',
    sections: [
      {
        h: "Quiénes Somos",
        body: "uCloud360 es una plataforma digital de incubación y aceleración operada por Inteligentic Solutions LLC, Miami, FL. Proporcionamos herramientas de colaboración, recursos educativos y utilidades tecnológicas para emprendedores hispanos en los Estados Unidos."
      },
      {
        h: "Información que Recopilamos",
        bullets: [
          "Información de cuenta: nombre, correo electrónico y datos de perfil que tú proporcionas.",
          "Datos de autenticación a través de Supabase Auth y proveedores OAuth opcionales.",
          "Datos de uso relacionados con herramientas y funciones de la plataforma.",
          "Datos técnicos: dirección IP, tipo de navegador, información del dispositivo y cookies.",
          "Los datos de facturación son procesados exclusivamente por Stripe — no almacenamos datos de tarjetas."
        ]
      },
      {
        h: "Cómo Usamos Tu Información",
        bullets: [
          "Para proporcionar, mantener y mejorar la plataforma.",
          "Para autenticar tu identidad y gestionar tu cuenta.",
          "Para procesar pagos de suscripción a través de Stripe.",
          "Para enviar correos transaccionales (verificación de email, recuperación de contraseña).",
          "Para analizar patrones de uso y mejorar el rendimiento."
        ]
      },
      {
        h: "Cookies y Seguimiento",
        body: "Usamos cookies y tecnologías similares para autenticar usuarios, mantener sesiones y analizar el rendimiento. Puedes deshabilitar las cookies en tu navegador, aunque esto puede afectar la funcionalidad."
      },
      {
        h: "Contenido Externo Embebido",
        body: "La plataforma incluye contenido de CoinGecko e Investing.com. Estos servicios pueden recopilar datos según sus propias políticas. No somos responsables de las prácticas de datos de terceros."
      },
      {
        h: "Almacenamiento y Transferencias Internacionales",
        body: "Tu información se almacena en la infraestructura de Supabase (alojada en AWS), y puede ser procesada en EE. UU. u otros países. Al usar uCloud360, consientes esta transferencia."
      },
      {
        h: "Tus Derechos (CCPA y Generales)",
        bullets: [
          "Derecho de acceso: solicita los datos personales que tenemos sobre ti.",
          "Derecho de corrección: solicita la corrección de datos inexactos.",
          "Derecho de eliminación: solicita la eliminación de tu cuenta y datos.",
          "Derecho de oposición: oponerte a procesamiento de datos no esencial.",
          "Para ejercer estos derechos: info@ucloud360.com."
        ]
      },
      {
        h: "Privacidad de Menores",
        body: "uCloud360 no está destinado a menores de 13 años."
      },
      {
        h: "Cambios a Esta Política",
        body: "Podemos actualizar esta Política periódicamente. Notificaremos cambios relevantes actualizando la fecha al inicio de esta página."
      },
      {
        h: "Contacto",
        body: "Para consultas de privacidad: info@ucloud360.com | uCloud360 · Inteligentic Solutions LLC · Miami, FL"
      }
    ]
  }
}

export default function PrivacyPage() {
  const { lang } = useLang()
  const t = CONTENT[lang]
  return <LegalPage icon={<Shield className="h-5 w-5" />} content={t} />
}
